import { supabase } from '../lib/supabase';
import { logger } from './loggingService';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
  max_projects: number;
  max_agents_per_project: number;
  max_tickets_per_month: number;
  max_executions_per_month: number;
  allowed_agent_providers: string[];
  has_advanced_analytics: boolean;
  has_audit_logs: boolean;
  priority_support: boolean;
  features: Record<string, unknown>;
}

interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  billing_cycle: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  current_period_start: string;
  current_period_end: string;
  trial_start?: string;
  trial_end?: string;
  canceled_at?: string;
  cancel_at_period_end: boolean;
  subscription_plans?: SubscriptionPlan;
}

interface SubscriptionUsage {
  projects_created: number;
  agents_created: number;
  tickets_created: number;
  executions_count: number;
  api_calls_count: number;
  storage_used_mb: number;
}

export class SubscriptionService {
  static async getPlans(): Promise<SubscriptionPlan[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      logger.error('Error fetching subscription plans', { error });
      return [];
    }

    return data || [];
  }

  static async getCurrentSubscription(userId: string): Promise<UserSubscription | null> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*, subscription_plans(*)')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) {
      logger.error('Error fetching current subscription', { error, userId });
      return null;
    }

    return data;
  }

  static async getCurrentPlan(userId: string): Promise<{ name: string; tier: string } | null> {
    const subscription = await this.getCurrentSubscription(userId);

    if (!subscription || !subscription.subscription_plans) {
      return { name: 'Free', tier: 'free' };
    }

    return {
      name: subscription.subscription_plans.name,
      tier: subscription.subscription_plans.name.toLowerCase(),
    };
  }

  static async getUsageForCurrentPeriod(userId: string): Promise<SubscriptionUsage | null> {
    const subscription = await this.getCurrentSubscription(userId);

    if (!subscription) {
      return null;
    }

    const { data, error } = await supabase
      .from('subscription_usage')
      .select('*')
      .eq('user_id', userId)
      .eq('subscription_id', subscription.id)
      .gte('period_start', subscription.current_period_start)
      .lte('period_end', subscription.current_period_end)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching usage', { error: error instanceof Error ? error.message : String(error), userId });
      return null;
    }

    return data;
  }

  /**
   * Check subscription limits (for UI display only)
   * Note: Actual enforcement happens at database RLS level
   * This method is for showing users their current usage
   */
  static async checkLimit(
    userId: string,
    resourceType: 'projects' | 'agents' | 'tickets' | 'executions'
  ): Promise<{ allowed: boolean; limit: number; current: number }> {
    try {
      const { data: isSuperAdmin } = await supabase.rpc('is_super_admin');

      if (isSuperAdmin) {
        return { allowed: true, limit: 999999, current: 0 };
      }

      const subscription = await this.getCurrentSubscription(userId);

      if (!subscription || !subscription.subscription_plans) {
        return { allowed: false, limit: 0, current: 0 };
      }

      const plan = subscription.subscription_plans;
      let limit = 0;

      switch (resourceType) {
        case 'projects':
          limit = plan.max_projects;
          break;
        case 'agents':
          limit = plan.max_agents_per_project;
          break;
        case 'tickets':
          limit = plan.max_tickets_per_month;
          break;
        case 'executions':
          limit = plan.max_executions_per_month;
          break;
      }

      let current = 0;

      if (resourceType === 'projects') {
        const { count } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .is('deleted_at', null);
        current = count || 0;
      } else {
        const usage = await this.getUsageForCurrentPeriod(userId);
        if (usage) {
          switch (resourceType) {
            case 'agents':
              current = usage.agents_created;
              break;
            case 'tickets':
              current = usage.tickets_created;
              break;
            case 'executions':
              current = usage.executions_count;
              break;
          }
        }
      }

      const allowed = current < limit;
      return { allowed, limit, current };
    } catch (error) {
      logger.error('Error checking subscription limit', { error: error instanceof Error ? error.message : String(error), userId, resourceType });
      return { allowed: false, limit: 0, current: 0 };
    }
  }

  /**
   * Atomically increment subscription usage
   * Uses database function to prevent race conditions
   */
  static async incrementUsage(
    userId: string,
    resourceType: 'projects' | 'agents' | 'tickets' | 'executions',
    amount: number = 1
  ): Promise<void> {
    try {
      // Call database function for atomic increment
      const { error } = await supabase.rpc('increment_subscription_usage', {
        p_user_id: userId,
        p_resource_type: resourceType,
        p_amount: amount,
      });

      if (error) {
        logger.error('Error incrementing usage', { error: error instanceof Error ? error.message : String(error), userId, resourceType });
        throw error;
      }
    } catch (error) {
      logger.error('Error incrementing usage', { error: error instanceof Error ? error.message : String(error), userId, resourceType });
      // Don't throw - usage tracking failure shouldn't block user actions
    }
  }

  static async canAccessFeature(userId: string, feature: string): Promise<boolean> {
    try {
      const subscription = await this.getCurrentSubscription(userId);

      if (!subscription || !subscription.subscription_plans) {
        return false;
      }

      const plan = subscription.subscription_plans;

      switch (feature) {
        case 'advanced_analytics':
          return plan.has_advanced_analytics;
        case 'audit_logs':
          return plan.has_audit_logs;
        case 'priority_support':
          return plan.priority_support;
        default:
          return true;
      }
    } catch (error) {
      logger.error('Error checking feature access', { error, userId, feature });
      return false;
    }
  }

  static async canUseAgentProvider(userId: string, provider: string): Promise<boolean> {
    try {
      const subscription = await this.getCurrentSubscription(userId);

      if (!subscription || !subscription.subscription_plans) {
        return false;
      }

      const allowedProviders = subscription.subscription_plans.allowed_agent_providers;
      return allowedProviders.includes(provider);
    } catch (error) {
      logger.error('Error checking provider access', { error, userId, provider });
      return false;
    }
  }

  static async upgradeSubscription(userId: string, planId: string): Promise<void> {
    logger.info('Upgrade subscription', { userId, planId });
  }

  static async cancelSubscription(userId: string, _reason?: string): Promise<void> {
    try {
      const subscription = await this.getCurrentSubscription(userId);

      if (!subscription) {
        throw new Error('No active subscription found');
      }

      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          cancel_at_period_end: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', subscription.id);

      if (error) throw error;
    } catch (error) {
      logger.error('Error canceling subscription', { error, userId });
      throw error;
    }
  }
}
